// src/pages/KnowledgeBasePage.jsx
// Knowledge Base shell — three main tabs:
//   Catalogues (Frameworks / Catalogues / Corporate Register / Baselines)
//   Policies
//   Model Fields
// Read-only browsing for everyone; manager-only edit affordances inside each tab.
//
// Risk taxonomy is methodology, not knowledge — it lives inside each Skill
// (assessment_templates.taxonomy JSONB) and is edited via /skills.

import { useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Tabs, Title, Text, Stack, Alert } from '@mantine/core';
import {
  IconBookmarks,
  IconFileText,
  IconForms,
  IconAlertCircle,
} from '@tabler/icons-react';

import UniversalFileUploader from '../UniversalFileUploader';
import ModelFormConfigEditor from '../globalConfigs/ModelFormConfigEditor';
import CataloguesTab from './knowledgeBase/CataloguesTab';
import { isRiskManager } from '../utils/roleHelpers';

export default function KnowledgeBasePage() {
  const { user } = useAuth0();
  const [activeTab, setActiveTab] = useState('catalogues');

  const userIsRiskManager = useMemo(() => {
    const userRoles =
      user?.[`${window.location.origin}/roles`] ||
      user?.['https://agenthuginn.ai/roles'] ||
      user?.['https://riskgpt.app/roles'] ||
      [];
    return isRiskManager(userRoles);
  }, [user]);

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        <Stack gap={4}>
          <Title order={2}>Knowledge Base</Title>
          <Text c="dimmed" size="sm">
            Reference content the agency draws on during assessments — frameworks, external risk catalogues, the corporate risk register, generated baselines, policies, taxonomy and model registration fields.
          </Text>
        </Stack>

        {!userIsRiskManager && (
          <Alert color="blue" icon={<IconAlertCircle size={16} />} variant="light">
            Read-only view. Editing is reserved for risk managers.
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="catalogues" leftSection={<IconBookmarks size={16} />}>
              Catalogues
            </Tabs.Tab>
            <Tabs.Tab value="policies" leftSection={<IconFileText size={16} />}>
              Policies
            </Tabs.Tab>
            <Tabs.Tab value="model-fields" leftSection={<IconForms size={16} />}>
              Model Fields
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="catalogues" pt="lg">
            <CataloguesTab />
          </Tabs.Panel>

          <Tabs.Panel value="policies" pt="lg">
            <UniversalFileUploader />
          </Tabs.Panel>

          <Tabs.Panel value="model-fields" pt="lg">
            <ModelFormConfigEditor />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
